import { z } from "zod";
import {
  IsoDateTime,
  LocalIdSchema,
  PositionSchema,
  SpotifyIdSchema,
  jsonColumn,
} from "./common.ts";

export const LocalPlaylistSchema = z.object({
  id: LocalIdSchema,
  name: z.string().min(1).max(300),
  folder_id: LocalIdSchema.nullable(),
  position: PositionSchema,
  /** Cloned from this Spotify playlist, if it started as a copy. */
  source_spotify_id: SpotifyIdSchema.nullable(),
  /** Most recent Spotify playlist created from this workspace list. */
  published_spotify_id: SpotifyIdSchema.nullable(),
  published_at: IsoDateTime.nullable(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});

export type LocalPlaylist = z.infer<typeof LocalPlaylistSchema>;

/** List rows carry a count so the sidebar does not need a second request. */
export const LocalPlaylistWithCountSchema = LocalPlaylistSchema.extend({
  track_count: z.number().int().nonnegative(),
  /**
   * First track cover in playlist order. Callers can later switch to a mosaic
   * when `cover_urls` lands — until then this is the single thumb.
   */
  cover_url: z.string().nullable(),
});

export type LocalPlaylistWithCount = z.infer<typeof LocalPlaylistWithCountSchema>;

export const LocalPlaylistCreateSchema = z.object({
  name: z.string().trim().min(1).max(300),
  folder_id: LocalIdSchema.nullable().optional(),
  source_spotify_id: SpotifyIdSchema.nullable().optional(),
  after_id: LocalIdSchema.nullable().optional(),
  before_id: LocalIdSchema.nullable().optional(),
  /** Seed the playlist in one call, e.g. when cloning or from a selection. */
  track_ids: z.array(SpotifyIdSchema).max(10000).optional(),
});

export type LocalPlaylistCreate = z.infer<typeof LocalPlaylistCreateSchema>;

export const LocalPlaylistUpdateSchema = z
  .object({
    name: z.string().trim().min(1).max(300),
    folder_id: LocalIdSchema.nullable(),
    after_id: LocalIdSchema.nullable(),
    before_id: LocalIdSchema.nullable(),
  })
  .partial();

export type LocalPlaylistUpdate = z.infer<typeof LocalPlaylistUpdateSchema>;

export const LocalPlaylistTrackSchema = z.object({
  local_playlist_id: LocalIdSchema,
  track_id: SpotifyIdSchema,
  /** REAL, fractionally indexed — reorder touches one row, never the whole list. */
  position: PositionSchema,
  added_at: IsoDateTime,
});

export type LocalPlaylistTrack = z.infer<typeof LocalPlaylistTrackSchema>;

export const LocalPlaylistAddTracksSchema = z.object({
  track_ids: z.array(SpotifyIdSchema).min(1).max(10000),
  after_id: SpotifyIdSchema.nullable().optional(),
  before_id: SpotifyIdSchema.nullable().optional(),
});

export const LocalPlaylistRemoveTracksSchema = z.object({
  track_ids: z.array(SpotifyIdSchema).min(1).max(10000),
});

export const LocalPlaylistMoveTrackSchema = z.object({
  track_id: SpotifyIdSchema,
  after_id: SpotifyIdSchema.nullable().optional(),
  before_id: SpotifyIdSchema.nullable().optional(),
});

/** Anything referencing a playlist's contents accepts these ids as JSON too. */
export const TrackIdListSchema = jsonColumn(z.array(SpotifyIdSchema));

/**
 * Always creates a new Spotify playlist (never updates in place).
 * `published_spotify_id` records the most recent one.
 */
export const LocalPlaylistPublishResultSchema = z.object({
  local_playlist: LocalPlaylistSchema,
  spotify_playlist_id: SpotifyIdSchema,
  spotify_url: z.string().url(),
  track_count: z.number().int().nonnegative(),
});

export type LocalPlaylistPublishResult = z.infer<typeof LocalPlaylistPublishResultSchema>;
